import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { Modal, Form, Button, Container, Row, Col, Card } from 'react-bootstrap';
import { loginUser, resetPassword, addNewUser, updateUserInterests } from '../redux/authSlice';
import Loader from '../components/Loader';
import { FaEye, FaEyeSlash, FaPlus, FaCheck } from 'react-icons/fa';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import '../css/auth.css';
import logo from '../images/logo.png';

const Login = ({ show, handleClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useRef(null);

  const { error: authError, loading: authLoading, user } = useSelector(state => state.auth);

  // Component states
  const [authMode, setAuthMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState([]);

  const interests = {
    poems: ["Happiness", "Sadness", "Fear", "Anger", "Disgust", "Surprise"],
    narratives: ["Story", "Novel"]
  };

  const toggleInterest = (item) => {
    setSelectedInterests(prev => 
      prev.includes(item)
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError('Invalid email address');
      return;
    }

    switch (authMode) {
      case 'signin':
        dispatch(loginUser({ email, password }))
          .unwrap()
          .then(() => {
            showToast('success', 'Success', 'Signed in successfully');
            
            // Check if user has interests, if not show recommendations
            if (!user?.interests || user.interests.length === 0) {
              setShowRecommendations(true);
            } else {
              navigate('/home');
              handleClose();
            }
          })
          .catch((error) => showToast('error', 'Error', error));
        break;

      case 'forgotPassword':
        dispatch(resetPassword(email))
          .unwrap()
          .then(() => {
            showToast('success', 'Success', 'Password reset email sent');
            setAuthMode('signin');
            setEmail('');
          })
          .catch((error) => showToast('error', 'Error', error));
        break;

      case 'signup':
        dispatch(addNewUser({ email, password, username, birthDate }))
          .unwrap()
          .then(() => {
            showToast('success', 'Success', 'Account created successfully');
            
            // After successful signup, login the user and show interests
            return dispatch(loginUser({ email, password })).unwrap();
          })
          .then(() => {
            setShowRecommendations(true);
          })
          .catch((error) => showToast('error', 'Error', error));
        break;

      default:
        break;
    }
  };

  const handleInterestsSubmit = () => {
    if (selectedInterests.length === 0) {
      showToast('warn', 'Warning', 'Please select at least one interest');
      return;
    }

    dispatch(updateUserInterests(selectedInterests))
      .unwrap()
      .then(() => {
        showToast('success', 'Success', 'Interests updated successfully');
        navigate('/home');
        setSelectedInterests([]);
        handleClose();
      })
      .catch((error) => showToast('error', 'Error', error));
  };

  // Show toast messages
  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail, life: 3000 });
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Render recommendations view
  const renderRecommendations = () => (
    <Container>
      <h5 className="text-center my-3">Choose your interests</h5>
      {Object.entries(interests).map(([category, items]) => (
        <div key={category} className="mb-4">
          <h5>{category.charAt(0).toUpperCase() + category.slice(1)}</h5>
          <Row>
            {items.map((item) => (
              <Col xs={6} sm={4} md={3} className="mb-3" key={item}>
                <Button
                  variant={selectedInterests.includes(item) ? "success" : "primary"}
                  className="w-100"
                  onClick={() => toggleInterest(item)}
                >
                  {selectedInterests.includes(item) ? <FaCheck /> : <FaPlus />} {item}
                </Button>
              </Col>
            ))}
          </Row>
        </div>
      ))}
      <div className="text-center mt-4">
        <Button variant="primary" onClick={handleInterestsSubmit} className="me-2">
          Submit Interests
        </Button>
      </div>
    </Container>
  );

  // Render form
  const renderForm = () => {
    if (showRecommendations) {
      return renderRecommendations();
    }

    return (
      <Form onSubmit={handleSubmit} className="auth-form">
        {authMode === 'signup' && (
          <>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Control
                type="date"
                placeholder="Birth Date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                required
              />
            </Form.Group>
          </>
        )}
        <Form.Group className="mb-3">
          <Form.Control
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setEmailError('');
            }}
            required
            isInvalid={!!emailError}
          />
          {emailError && <Form.Text className="text-danger">{emailError}</Form.Text>}
        </Form.Group>
        {authMode !== 'forgotPassword' && (
          <Form.Group className="mb-3 position-relative">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Button
              variant="link"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </Button>
          </Form.Group>
        )}
        <Button variant="primary" type="submit" className="w-100 auth-submit-btn">
          {authMode === 'signin' ? 'Sign In' : authMode === 'signup' ? 'Sign Up' : 'Reset Password'}
        </Button>
      </Form>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="auth-modal" style={{ background: 'linear-gradient(135deg, #babcf0, #9c9fd0)' }}>
      <Modal.Header closeButton>
        <Modal.Title>{showRecommendations ? 'Select Your Interests' : 'Please login to Like or Comment on Content'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Container fluid>
          <Row className="h-100">
           {!showRecommendations &&
            <Col md={6} className="auth-banner d-flex align-items-center justify-content-center">
              <div className="text-center">
                <div className="text-center mb-3">
                  <LazyLoadImage
                    src={logo}
                    alt="Login Logo"
                    effect="blur"
                    className="img-fluid"
                    style={{ maxHeight: '100px', width: 'auto' }}
                  />
                </div>
              </div>
            </Col>
            }
            {/* <Col md={6} className="auth-form-container p-5"> */}
            <Col md={showRecommendations ? 12 : 6} className={`auth-form-container p-5 ${showRecommendations ? 'w-100' : ''}`}>
              <Toast ref={toast} />
              {authLoading ? (
                <Loader loadingMessage="Processing..." />
              ) : (
                <Card className="auth-card">
                  <Card.Body>
                    <div className="text-center mb-4 auth-title fs-3">
                      {showRecommendations 
                        ? 'Select Your Interests' 
                        : (authMode === 'signin' ? 'Sign In' : authMode === 'signup' ? 'Sign Up' : 'Reset Password')
                      }
                    </div>
                    {renderForm()}
                    {authError && <p className="text-danger mt-3">{authError}</p>}
                    {!showRecommendations && (
                      <div className="mt-3 text-center auth-links">
                        {authMode === 'signin' && (
                          <>
                            <Button variant="link" onClick={() => setAuthMode('forgotPassword')}>
                              Forgot password?
                            </Button>
                            <Button variant="link" onClick={() => setAuthMode('signup')}>
                              Sign Up
                            </Button>
                          </>
                        )}
                        {authMode === 'forgotPassword' && (
                          <Button variant="link" onClick={() => setAuthMode('signin')}>
                            Back to Sign In
                          </Button>
                        )}
                        {authMode === 'signup' && (
                          <Button variant="link" onClick={() => setAuthMode('signin')}>
                            Already have an account? Sign In
                          </Button>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

export default Login;